import React, { Fragment, useEffect, useState, useRef } from "react";
import {
  useHistory,
} from "react-router-dom";
import { Button } from "react-bootstrap";
import { db } from "../../../../firebase";
import {
  collection,
  query,
  orderBy,
  where,
  and,
  getDocs,
  documentId,
  doc,
  updateDoc,
} from "firebase/firestore";
import swal from "sweetalert";
import {
  notifyTopRight,
  notifyError,
} from "../../toast/SetToast";
import Toast from "../../toast/SetToast";

import PageTitle from "../../../layouts/PageTitle";
import MaterialIssueItem from "./MaterialIssueItem";
import MaterialIssueRegister from "./MaterialIssueRegister";
import MaterialIssueDetail from "./MaterialIssueDetail";

import { usePagination } from "../../../../hook/usePagination";
import { useQuery } from "../../../../hook/useQuery";
import { useAuth } from "../../../../context/AuthContext";

const MaterialIssueList = () => {
  const { currentUser } = useAuth();
  const canCreateMaterialIssue =
    currentUser &&
    Array.isArray(currentUser.roles) &&
    currentUser.roles.some((role) =>
      ["super_admin", "sale", "accountant", "admin"].includes(role)
    );
  const [issueList, setIssueList] = useState([]);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isApprover, setIsApprover] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const history = useHistory();
  const searchParams = useQuery();
  const param = searchParams.get("q");
  const itemPerPage = 20;

  const searchRef = useRef(null);

  // Check if current user is an available approver
  useEffect(() => {
    const checkApprover = async () => {
      if (!currentUser) return;
      try {
        const uidQ = query(
          collection(db, "approvers"),
          where("uid", "==", currentUser.uid)
        );
        const uidSnap = await getDocs(uidQ);

        for (const docSnap of uidSnap.docs) {
          const data = docSnap.data();
          const isCurrentUser =
            data.name === currentUser.displayName ||
            data.email === currentUser.email;
          if (isCurrentUser && data.status === "Available") {
            setIsApprover(true);
            return;
          }
        }

        // Fallback: match by displayName (for older docs without uid)
        if (currentUser.displayName) {
          const nameQ = query(
            collection(db, "approvers"),
            where("name", "==", currentUser.displayName)
          );
          const nameSnap = await getDocs(nameQ);
          for (const docSnap of nameSnap.docs) {
            if (docSnap.data().status === "Available") {
              setIsApprover(true);
              return;
            }
          }
        }
      } catch (err) {
        console.error("Error checking approver status:", err);
      }
    };
    checkApprover();
  }, [currentUser]);

  // Build query based on filters
  let QueryFilterConstraint = [];
  let queryRef = query(
    collection(db, "materialIssues"),
    orderBy("createdAt", "desc")
  );

  if (statusFilter || typeFilter || param) {
    if (statusFilter) {
      QueryFilterConstraint.push(where("status", "==", statusFilter));
    }
    if (typeFilter) {
      QueryFilterConstraint.push(where("materialType", "==", typeFilter));
    }
    if (param) {
      QueryFilterConstraint.push(
        where("requestNo", ">=", param),
        where("requestNo", "<=", param + "\uf8ff")
      );
    }

    if (QueryFilterConstraint.length > 0) {
      queryRef = query(
        collection(db, "materialIssues"),
        and(...QueryFilterConstraint),
        orderBy("createdAt", "desc")
      );
    }
  }

  const {
    items: materialIssueInfo,
    isLoading,
    isStart,
    isEnd,
    getPrev,
    getNext,
    totalItems,
    totalPages,
    currentPage,
    goToPage,
  } = usePagination(queryRef, { limit: itemPerPage });

  const handleSearchChange = () => {
    history.replace({
      search: new URLSearchParams({ q: searchRef.current.value }).toString(),
    });
  };

  const handleOnNewRequest = () => {
    if (!canCreateMaterialIssue) {
      notifyError("You do not have permission to create material issue requests.");
      return;
    }
    setShowRegisterModal(true);
  };

  const handleOnView = (id) => {
    const found = issueList.find((c) => c.id === id);
    setSelectedItem(found);
    setShowDetailModal(true);
  };

  const handleOnRegisterHide = () => {
    setShowRegisterModal(false);
  };

  const handleOnDetailHide = () => {
    setShowDetailModal(false);
    setSelectedItem(null);
  };

  const handleCancel = (id) => {
    swal({
      title: "Cancel this request?",
      text: "This will cancel your material issue request.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willCancel) => {
      if (!willCancel) return;
      try {
        await updateDoc(doc(db, "materialIssues", id), {
          status: "Cancelled",
          cancelledBy: currentUser.displayName || currentUser.email,
          cancelledAt: new Date(),
          updatedAt: new Date(),
        });
        notifyTopRight("ยกเลิกคำขอสำเร็จ");
      } catch (err) {
        console.error(err);
        notifyError("เกิดข้อผิดพลาด: " + err.message);
      }
    });
  };

  useEffect(() => {
    let isCurrent = true;

    const loadIssueItemDetails = async () => {
      setIssueList(materialIssueInfo);

      const accessoryIds = [
        ...new Set(
          materialIssueInfo
            .filter(
              (issue) =>
                issue.materialType === "Accessory" && issue.itemId
            )
            .map((issue) => issue.itemId)
        ),
      ];
      const cpapIds = [
        ...new Set(
          materialIssueInfo
            .filter(
              (issue) => issue.materialType === "CPAP" && issue.itemId
            )
            .map((issue) => issue.itemId)
        ),
      ];

      if (accessoryIds.length === 0 && cpapIds.length === 0) return;

      try {
        const [accessoriesSnapshot, productsSnapshot] = await Promise.all(
          [
            accessoryIds.length > 0
              ? getDocs(
                  query(
                    collection(db, "accessories"),
                    where(documentId(), "in", accessoryIds)
                  )
                )
              : Promise.resolve(null),
            cpapIds.length > 0
              ? getDocs(
                  query(
                    collection(db, "products"),
                    where(documentId(), "in", cpapIds)
                  )
                )
              : Promise.resolve(null),
          ]
        );
        const itemNamesById = new Map(
          (accessoriesSnapshot ? accessoriesSnapshot.docs : []).map(
            (accessoryDoc) => [
              accessoryDoc.id,
              accessoryDoc.data().itemName || accessoryDoc.data().name,
            ]
          )
        );
        const cpapDetailsById = new Map(
          (productsSnapshot ? productsSnapshot.docs : []).map((productDoc) => {
            const productData = productDoc.data();
            const itemName = `${productData.brand || ""} ${
              productData.model || ""
            }`.trim();

            return [
              productDoc.id,
              {
                itemName: itemName || productData.serialNumber || "",
                itemDetails: productData.serialNumber || "",
              },
            ];
          })
        );

        if (!isCurrent) return;

        setIssueList(
          materialIssueInfo.map((issue) => {
            if (issue.materialType === "Accessory") {
              return {
                ...issue,
                itemName: itemNamesById.get(issue.itemId) || issue.itemName,
              };
            }

            if (issue.materialType === "CPAP") {
              const cpapDetails = cpapDetailsById.get(issue.itemId);

              return {
                ...issue,
                itemName: cpapDetails
                  ? cpapDetails.itemName || issue.itemName
                  : issue.itemName,
                itemDetails: cpapDetails
                  ? cpapDetails.itemDetails || issue.itemDetails
                  : issue.itemDetails,
              };
            }

            return issue;
          })
        );
      } catch (err) {
        console.error("Error loading material issue item details:", err);
      }
    };

    loadIssueItemDetails();

    return () => {
      isCurrent = false;
    };
  }, [materialIssueInfo]);

  return (
    <Fragment>
      <Toast />
      <PageTitle activeMenu="Material Issue" motherMenu="Sales" />
      <div className="form-head d-flex mb-3 mb-lg-5 align-items-center flex-wrap">
        {canCreateMaterialIssue && (
          <Button
            onClick={handleOnNewRequest}
            className="btn btn-danger mr-3"
            data-toggle="tooltip"
            data-placement="top"
            title="New Material Issue Request"
          >
            + New Request
          </Button>
        )}

        {/* Status Filter */}
        <select
          className="form-control mr-2"
          style={{ maxWidth: "160px" }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All Status</option>
          <option value="Pending">Pending</option>
          <option value="Approved">Approved</option>
          <option value="Rejected">Rejected</option>
          <option value="Cancelled">Cancelled</option>
        </select>

        {/* Type Filter */}
        <select
          className="form-control mr-2"
          style={{ maxWidth: "160px" }}
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option value="">All Types</option>
          <option value="Accessory">Accessory</option>
          <option value="CPAP">CPAP</option>
        </select>

        <div className="input-group search-area ml-auto d-inline-flex">
          <input
            type="text"
            className="form-control"
            placeholder="Search Request No"
            onChange={handleSearchChange}
            ref={searchRef}
          />
          <div className="input-group-append">
            <span className="input-group-text c-pointer">
              <i className="flaticon-381-search-2"></i>
            </span>
          </div>
        </div>
      </div>

      <MaterialIssueRegister
        show={showRegisterModal}
        hide={handleOnRegisterHide}
        canCreate={canCreateMaterialIssue}
      />

      {selectedItem && (
        <MaterialIssueDetail
          item={selectedItem}
          show={showDetailModal}
          hide={handleOnDetailHide}
          isApprover={isApprover}
        />
      )}

      {!isLoading && (
        <MaterialIssueItem
          items={issueList}
          onView={handleOnView}
          onCancel={handleCancel}
          currentUser={currentUser}
          totalItems={totalItems}
          totalPages={totalPages}
          currentPage={currentPage}
          itemsPerPage={itemPerPage}
          goToPage={goToPage}
          isStart={isStart}
          isEnd={isEnd}
          getNext={getNext}
          getPrev={getPrev}
        />
      )}
    </Fragment>
  );
};

export default MaterialIssueList;
